import React, { useEffect, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { Button, DatePicker, Modal, Spinner, Steps, UploadFile } from 'ui';
import { ButtonListModal, StyledDivider, StyledPage } from 'styles/layout/form';
import { Flex, Form, Row, Space } from 'antd';
import FirstStep from './steps/FirstStep';
import SecondStep from './steps/SecondStep';
import useQueryApiClient from 'utils/useQueryApiClient';
import { useNavigate, useParams } from 'react-router-dom';
import { pages } from 'constants/pages';
import DefaultLayout from 'components/DefaultLayout';
import ThirdStep from './steps/ThirdStep';
import dayjs from 'dayjs';
import { useUserState } from 'contexts/UserContext';
import CustomGeoProductActionModal from './modals/CustomGeoProductActionModal';
import MetadataResponseModal, { MetadataStatus } from './modals/MetadataResponseModal';
import useHandleError from 'utils/useHandleError';
import useModal from 'antd/lib/modal/useModal';

export type MetadataResponse = {
  report: string;
  status: MetadataStatus;
};

export type DivEndIndex = {
  servicesEndIndex: number;
  filesEndIndex: number;
  othersEndIndex: number;
};

const enableInspireValidationOnPublish = window?.runConfig?.enableInspireValidationOnPublish;

const GeoProductCreateEditPage = () => {
  const [currentStep, setCurrentStep] = useState<number>(0);
  const [showConfirmModal, setShowConfirmModal] = useState<boolean>(false);
  const [showCancelModal, setShowCancelModal] = useState<boolean>(false);
  const [showPublishModal, setShowPublishModal] = useState<boolean>(false);
  const [wantedToPublish, setWantedToPublish] = useState<boolean>(false);

  const [divEndIndex, setDivEndIndex] = useState<DivEndIndex>({
    servicesEndIndex: 0,
    filesEndIndex: 0,
    othersEndIndex: 0,
  });

  const { id } = useParams();
  const [_id, setId] = useState<string | undefined>(id);

  const [isInspired, setIsInspired] = useState(false);
  const [showMetadataModal, setShowMetadataModal] = useState(false);
  const [metadataEID, setMetadataEID] = useState<string>();
  const [inspireLoopIsWorking, setInspireLoopIsWorking] = useState(false);
  const [openTabs, setOpenTabs] = useState<number[]>([]);
  const [metadataResponse, setMetadataResponse] = useState<MetadataResponse | undefined>();
  const firstInspireLoopPassed = useRef(false);
  const timePopupOpened = useRef(false);
  const publishing = useRef(false);

  const user = useUserState();
  const [handleError] = useHandleError();
  const intl = useIntl();
  const [form] = Form.useForm();
  const [publishForm] = Form.useForm();
  const navigate = useNavigate();
  const formValues = form.getFieldsValue(true);
  const activeRole = user.roles.find((e) => e.id === user.selectedRole);
  const [modal, contextHolder] = useModal();

  // eslint-disable-next-line no-empty-pattern
  const {
    data: geoproductData,
    refetch,
    isLoading: isRefetchLoading,
  } = useQueryApiClient({
    request: {
      url: `api/v1/geoproducts/${id}`,
    },
    enabled: !!parseInt(id!),
    onSuccess: (values) => {
      const parse = {
        ...values,
        dataReleaseDate: values.dataReleaseDate && dayjs(values.dataReleaseDate),
        dataUpdatedAt: values.dataUpdatedAt && dayjs(values.dataUpdatedAt),
        publicFrom: !!values.publicFrom ? dayjs(values.publicFrom) : null,
        publicTo: !!values.publicTo ? dayjs(values.publicTo) : null,
        filesData: values.filesData.map((e: any) => {
          let parsed = { ...e };

          if (parsed.frequencyDate) {
            parsed.frequencyDate = dayjs(parsed.frequencyDate);
          }

          return parsed;
        }),
      };

      form.setFieldsValue(parse);
      setIsInspired(values.isInspired);

      const servicesEndIndex = values?.services?.length;
      const filesEndIndex = servicesEndIndex + values?.filesData?.length;
      const othersEndIndex = filesEndIndex + values?.others?.length;

      setDivEndIndex({
        servicesEndIndex,
        filesEndIndex,
        othersEndIndex,
      });
    },
  });

  let intervalId: NodeJS.Timer;

  const { appendData: getInspireResponse } = useQueryApiClient({
    request: {
      url: `api/v1/geoproducts/${metadataEID}/inspire-response`,
      method: 'POST',
      disableOnMount: true,
    },
    onSuccess: (res: MetadataResponse | undefined) => {
      setMetadataResponse(res);

      if (!!id && publishing.current && !!res && !!Object.keys(res).length) {
        const publicTo = publishForm.getFieldValue('publicTo');
        const publicFrom = publishForm.getFieldValue('publicFrom');
        publishAppendData({ publicTo, publicFrom });
      } else if (!id && !!_id && !!res && !!Object.keys(res).length) {
        navigate(`/geoproducts/${_id}`);
      }
    },
    onError: () => {
      resetInspireLoopCheck();
      clearInterval(intervalId);
    },
  });

  // Wait for the response from inspire.
  // If first response is empty show modal which says validation can take up to 1 minute.
  // Else show modal with info accordingly to response from inspire.
  useEffect(() => {
    if (!enableInspireValidationOnPublish && publishing.current) return;
    if (!isInspired) return;

    if (!!metadataResponse && !!Object.keys(metadataResponse).length) {
      clearInterval(intervalId);
      setShowMetadataModal(true);
      setInspireLoopIsWorking(false);
      firstInspireLoopPassed.current = false;
    } else if (metadataEID) {
      intervalId = setInterval(() => {
        getInspireResponse({ geoProductId: _id });
        if (!firstInspireLoopPassed.current) firstInspireLoopPassed.current = true;
      }, 5000);
    }

    if (firstInspireLoopPassed.current && !timePopupOpened.current) {
      setShowMetadataModal(true);
      timePopupOpened.current = true;
    }

    return () => {
      clearInterval(intervalId);
    };
  }, [metadataEID, metadataResponse, isInspired]);

  const { appendData: updateAppendData, isLoading: isUpdateLoading } = useQueryApiClient({
    request: {
      url: `api/v1/geoproducts/${id}?_method=PATCH`,
      method: 'POST',
      formData: true,
    },
    onSuccess: (res) => {
      refetch();
      setIsInspired(res.isInspired);

      if (res.isInspired && enableInspireValidationOnPublish) {
        setMetadataEID(res.inspireValidation);
        setInspireLoopIsWorking(true);
      } else if (publishing.current) {
        // Publish geo product.
        const [publicTo, publicFrom] = [
          publishForm.getFieldValue('publicTo'),
          publishForm.getFieldValue('publicFrom'),
        ].map((date: any) => (date ? date.format('DD.MM.YYYY') : date));

        publishAppendData({ publicTo, publicFrom });
      }
    },
    onError: () => {
      resetInspireLoopCheck();
      setInspireLoopIsWorking(false);
    },
    onFinally: () => resetToBeDeleted(),
  });

  const resetToBeDeleted = () => {
    form.setFieldValue('toBeDeleted', []);
  };

  const { appendData: publishAppendData, isLoading: isPublishLoading } = useQueryApiClient({
    request: {
      url: `api/v1/geoproducts/${id}/publish`,
      method: 'POST',
    },
    onSuccess: () => navigate(pages.geoproduct.url),
    onError: (e) => {
      setShowPublishModal(false);
      resetInspireLoopCheck();
      setInspireLoopIsWorking(false);
      setWantedToPublish(true);

      if (e.code === 422) {
        setOpenTabs([1]);
        setCurrentStep(0);
      }
    },
    onFinally: () => {
      publishing.current = false;
    },
  });

  const { appendData, isLoading } = useQueryApiClient({
    request: {
      url: `api/v1/geoproducts`,
      method: 'POST',
      formData: true,
    },
    onSuccess: (res) => {
      setId(res.id);
      setIsInspired(res.isInspired);

      if (res.isInspired && enableInspireValidationOnPublish) {
        setMetadataEID(res.inspireValidation);
        setInspireLoopIsWorking(true);
      } else if (!id) {
        navigate(`/geoproducts/${res.id}`);
      }
    },
    onError: () => {
      resetInspireLoopCheck();
      setInspireLoopIsWorking(false);
    },
  });

  useEffect(() => {
    if (wantedToPublish) {
      setTimeout(() => {
        form.validateFields();
      }, 100);
    }
  }, [currentStep]);

  const resetInspireLoopCheck = () => {
    if (!!metadataResponse && !!Object.keys(metadataResponse).length) {
      setMetadataEID(undefined);
      setMetadataResponse(undefined);
      setInspireLoopIsWorking(false);
      firstInspireLoopPassed.current = false;
      timePopupOpened.current = false;
      publishing.current = false;
    }
  };

  useEffect(() => {
    if (!formValues['organizationName'])
      form.setFieldValue('organizationName', user.roles.find((e) => e.id === user.selectedRole)?.institutionName);
    if (!formValues['email']) form.setFieldValue('email', activeRole?.email);
  }, [formValues, user.selectedRole, activeRole?.email, user.roles]);

  const items = [
    {
      title: intl.formatMessage({ id: 'geoproducts.basic_information' }),
    },
    {
      title: intl.formatMessage({ id: 'geoproducts.type_of_distribution' }),
    },
    {
      title: intl.formatMessage({ id: 'geoproducts.summary' }),
    },
  ];

  const stepsElements = [
    <FirstStep inspireMetadata={isInspired} setInspireMetadata={setIsInspired} form={form} openTabs={openTabs} />,
    <SecondStep form={form} id={id} divEndIndex={divEndIndex} setDivEndIndex={setDivEndIndex} />,
    <ThirdStep form={form} />,
  ];

  const onPublish = () => {
    setShowPublishModal(true);
    publishing.current = true;
  };

  const openConfirmModal = (title: string, onOk: Function) => {
    const confirmModal = modal.confirm({
      title,
      footer: (
        <Flex justify="end" gap={12}>
          <Button label={intl.formatMessage({ id: 'general.close' })} onClick={() => confirmModal.destroy()} />
          <Button
            type="primary"
            label={intl.formatMessage({ id: 'general.unpublish' })}
            onClick={() => {
              confirmModal.destroy();
              onOk();
            }}
          />
        </Flex>
      ),
    });
  };

  const onSubmit = async (e: any, doValidate: boolean = true) => {
    const values = JSON.parse(JSON.stringify(form.getFieldsValue(true))); // create deep copy

    try {
      doValidate && values.isPublic && validateServices(values);
    } catch (e: any) {
      openConfirmModal(e.message, () => onSubmit(undefined, false));

      return;
    }

    let bodyFormData = new FormData();

    try {
      Object.entries(values).forEach((entry: any) => {
        const [fieldName, data]: [string, any] = entry;

        switch (true) {
          case ['photo', 'dataSpecification'].includes(fieldName):
            const files = data as UploadFile[];

            bodyFormData.append(fieldName, JSON.stringify([files.pop()?.id || null]));

            break;
          case ['dataReleaseDate', 'dataUpdatedAt'].includes(fieldName) && !!data:
            bodyFormData.append(fieldName, dayjs(data).format('DD.MM.YYYY') || '');

            break;
          case ['services', 'filesData'].includes(fieldName):
            const [_, items]: [string, any[]] = entry;

            items.forEach((innerEntry: any, index: number) => {
              ['licence', 'file', 'atom'].forEach((field) => {
                if (innerEntry?.[field]) {
                  const attachments = Array.isArray(items[index][field]) ? items[index][field] : [];

                  items[index][field] =
                    attachments
                      .map((file: UploadFile) => {
                        if (['error', 'uploading'].includes(file?.status || '')) {
                          throw new Error('validation.file_upload_error');
                        }

                        return file && file.id;
                      })
                      .filter(Boolean) || [];
                }
              });
            });
            appendObject(bodyFormData, fieldName, data);

            break;
          case typeof data === 'object':
            appendObject(bodyFormData, fieldName, data);

            break;
          default:
            bodyFormData.append(fieldName, data);
        }
      });

      if (showPublishModal && id) {
        setShowPublishModal(false);
        updateAppendData(bodyFormData);
      } else if (id) {
        updateAppendData(bodyFormData);
      } else {
        appendData(bodyFormData);
      }
    } catch ({ message }) {
      handleError({ error: message as string });
    }
  };

  const appendObject = (bodyFormData: FormData, fieldName: string, data: any) => {
    bodyFormData.append(fieldName, JSON.stringify(data));
  };

  const validateServices = ({ services, others, filesData, none }: any) => {
    const hasPublicService = [...services, ...others, ...filesData, ...none].find(({ isPublic }) => isPublic);

    if (!hasPublicService) {
      throw new Error(intl.formatMessage({ id: 'geoproducts.will_be_unpublished' }));
    }
  };

  const handleSteps = async (type: 'add' | 'remove') => {
    if (type === 'add') {
      setCurrentStep(currentStep + 1);
    }

    if (type === 'remove') {
      setCurrentStep(currentStep - 1);
    }
  };

  const onCancelCheck = () => {
    if (form.isFieldsTouched([], true)) {
      setShowCancelModal(true);
      return;
    }

    onCancel();
  };

  const onCancel = () => {
    setShowCancelModal(false);
    navigate('/geoproducts');
  };

  //check if is changed
  const onBackCheck = () => {
    const isTouched = form.isFieldsTouched(['services', 'filesData', 'others', 'none']);

    if (isTouched) {
      setShowConfirmModal(true);
      return;
    }

    onBack();
  };

  const onBack = () => {
    if (currentStep === 1) {
      refetch();
    }

    setShowConfirmModal(false);
    setCurrentStep(currentStep - 1);
  };

  return (
    <DefaultLayout.PageLayout>
      <DefaultLayout.PageContent>
        <StyledPage>
          <DefaultLayout.PageHeader
            title={intl.formatMessage({
              id: id ? pages.geoproduct.edit.title : pages.geoproduct.create.title,
            })}
            breadcrumb={[
              {
                path: pages.geoproduct.url,
                name: intl.formatMessage({ id: pages.geoproduct.title }),
              },
            ]}
          />
          <Steps
            onChange={(current) => setCurrentStep(current)}
            current={currentStep}
            labelPlacement="vertical"
            items={items}
          />
          <StyledDivider />
          <Spinner
            spinning={isLoading || isPublishLoading || isUpdateLoading || isRefetchLoading || inspireLoopIsWorking}
          >
            <Form form={form} layout="vertical" onFinish={onSubmit}>
              {!(isLoading || isPublishLoading || isUpdateLoading || isRefetchLoading || inspireLoopIsWorking) &&
                stepsElements[currentStep]}
              <Row justify="space-between">
                <div>
                  {currentStep !== 0 && (
                    <Button label={intl.formatMessage({ id: 'general.back' })} onClick={() => onBackCheck()} />
                  )}
                </div>
                <Space size={10}>
                  {currentStep === 2 ? (
                    <>
                      <Button label={intl.formatMessage({ id: 'general.submit' })} onClick={onSubmit} />
                      {!!id && geoproductData?.status === 'DRAFT' && (
                        <Button
                          type="primary"
                          label={intl.formatMessage({ id: 'general.publish' })}
                          onClick={onPublish}
                        />
                      )}
                    </>
                  ) : (
                    <>
                      <Button label={intl.formatMessage({ id: 'general.cancel' })} onClick={onCancelCheck} />
                      <Button label={intl.formatMessage({ id: 'general.submit' })} onClick={onSubmit} />
                      <Button
                        type="primary"
                        label={intl.formatMessage({ id: 'general.continue' })}
                        onClick={() => handleSteps('add')}
                      />
                    </>
                  )}
                </Space>
              </Row>
              <Modal
                open={showPublishModal}
                footer={null}
                onCancel={() => setShowPublishModal(true)}
                title={intl.formatMessage({ id: 'geoproducts.publish_geoproduct' })}
              >
                <Form form={publishForm} layout={'vertical'}>
                  <DatePicker label={intl.formatMessage({ id: 'geoproducts.date_from' })} name="publicFrom" />
                  <DatePicker label={intl.formatMessage({ id: 'geoproducts.date_to' })} name="publicTo" />
                </Form>

                <Row justify="end">
                  <ButtonListModal>
                    <Button
                      label={intl.formatMessage({
                        id: 'general.cancel',
                      })}
                      onClick={() => setShowPublishModal(false)}
                    />
                    <Button
                      type="primary"
                      label={intl.formatMessage({
                        id: 'general.publish',
                      })}
                      htmlType="submit"
                    />
                  </ButtonListModal>
                </Row>
              </Modal>
            </Form>
          </Spinner>
          <CustomGeoProductActionModal
            setShowModal={setShowConfirmModal}
            showModal={showConfirmModal}
            manualOnModalContinue={onBack}
          />
          <CustomGeoProductActionModal
            confirmTextId="general.save.cancel"
            cancelButtonTextId="general.cancel"
            continueButtonTextId="general.submit"
            setShowModal={setShowCancelModal}
            showModal={showCancelModal}
            manualOnModalContinue={onSubmit}
            manualOnModalCancel={onCancel}
          />
          <MetadataResponseModal
            setShowModal={setShowMetadataModal}
            showModal={showMetadataModal}
            metadataResponse={metadataResponse}
            resetInspireLoopCheck={resetInspireLoopCheck}
            creatingNew={id === undefined}
          />
          {contextHolder}
        </StyledPage>
      </DefaultLayout.PageContent>
    </DefaultLayout.PageLayout>
  );
};

export default GeoProductCreateEditPage;
